import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { AICCBuilderInitialConfig } from "constants/AICCBuilderConfig";
import { AICCArithmeticSelector } from "./core/AICCArithmeticSelector";
import AICCFieldSelector from "./core/AICCFieldSelector";
import AICCRuleOperator from "./core/AICCRuleOperator";
import AICCValueSource from "./core/AICCValueSource";
import AICCValueEditor from "./value/AICCValueEditor";
import { getWidgetTypeBasedOnFieldType } from "utils/coreUtils";
import { useRuleDataContext } from "../../../context/RuleDataProvider";
import { RuleVersionCrudType } from "../../../types/index";

/**
 * {
 *  data: {
 *    id: "rule id",
 *    expressionId: "if expression is saved then it will be passed",
 *    group: "group id",
 *    selectedLeftArOp: "left side selected arithmetic operator",
 *    leftArOpValue: "left arithmetic value",
 *    selectedRightArOp: "left side selected arithmetic operator",
 *    rightArOpValue: "right arithmetic value",
 *    column: "column name",
 *    operator: "operator name",
 *    valueSource: "value | field"  // default value
 *    expectedValue: "expected value",
 *    midOperator: "AND | OR",
 *  },
 * index: 0, // this will be index of clause
 * }
 */

export default function AICCRule({
  data,
  useColumnGroup,
  columnsWithGroup = [],
  columnList = [],
  operatorList = AICCBuilderInitialConfig.operators,
  onRemove,
  onChange,
  allowValueSource,
  allowArithmetic,
  isCaseCheckEnable = true,
  isRuleComplete = false,
  evaluationClass = "",
  evaluationValues,
}) {
  const [t] = useTranslation();
  const fieldSelectRef = useRef();
  const operatorSelectRef = useRef();
  const { type } = useRuleDataContext();
  const viewOnly = type === RuleVersionCrudType.VIEW;
  const [isCaseInsensitiveCheck, setIsCaseInsensitiveCheck] = useState(
    data.isCaseInsensitiveCheck ?? false
  );
  const onChangeField = (key, value) => {
    if (onChange) {
      onChange({
        ...data,
        [key]: value,
      });
    }
  };

  const onChangeMultipleField = (obj) => {
    if (onChange) {
      onChange({
        ...data,
        ...obj,
      });
    }
  };

  const finalColumnList = useMemo(
    () =>
      useColumnGroup
        ? (columnsWithGroup.find((e) => "" + e.value === "" + data?.group)
            ?.subFields ?? [])
        : columnList,
    [columnList, columnsWithGroup, data?.group, useColumnGroup]
  );
  const selectedField = useMemo(
    () => finalColumnList.find((e) => "" + e.value === "" + data?.column),
    [data?.column, finalColumnList]
  );

  const operatorObj = useMemo(
    () => operatorList.find((e) => e.value === data?.operator),
    [data?.operator, operatorList]
  );

  const finalOperatorList = useMemo(
    () =>
      operatorList.filter((e) =>
        getWidgetTypeBasedOnFieldType(selectedField?.type) === "any"
          ? true
          : e.type === getWidgetTypeBasedOnFieldType(selectedField?.type) ||
            e.value === data?.operator
      ),
    [data?.operator, operatorList, selectedField?.type]
  );

  const showValueSource = useMemo(
    () =>
      selectedField &&
      operatorObj &&
      (!["_in"].some((e) => operatorObj.value.includes(e)) ||
        operatorObj.type === "text") &&
      !operatorObj?.noInput &&
      allowValueSource,
    [allowValueSource, operatorObj, selectedField]
  );

  return (
    <div className="aicc-ruleBuilder__rule">
      <div
        className={`aicc-ruleBuilder__rule--main-container align-items-center ${evaluationClass} ${!isRuleComplete ? "error" : ""}`}
      >
        <div>
          {useColumnGroup ? (
            <div className="col-auto aicc-ruleBuilder__rule--fields-group">
              <AICCFieldSelector
                columnList={columnsWithGroup}
                value={data?.group}
                placeholder={t("aiccRuleBuilder.fieldStreamPlaceholder")}
                noOptionsLabel={t("aiccRuleBuilder.noFieldStreamOptions")}
                onChange={(e) => {
                  onChangeMultipleField({
                    group: e?.value,
                  });

                  setTimeout(() => {
                    if (fieldSelectRef?.current) {
                      fieldSelectRef.current.clearSelect();
                    }
                  }, 10);
                }}
                disabled={viewOnly}
              />
            </div>
          ) : null}
          <div className="col-auto aicc-ruleBuilder__rule--fields">
            <AICCFieldSelector
              columnList={finalColumnList}
              value={data?.column}
              ref={fieldSelectRef}
              placeholder={t("aiccRuleBuilder.fieldPlaceholder")}
              noOptionsLabel={t("aiccRuleBuilder.noFieldOptions")}
              disabled={viewOnly}
              evaluationValues={evaluationValues}
              onChange={(e) => {
                const oldField = finalColumnList.find(
                  (item) => item.value === data?.column
                );

                onChangeMultipleField({
                  column: e?.value,
                });

                setTimeout(() => {
                  if (oldField?.type !== e?.type) {
                    if (operatorSelectRef?.current) {
                      operatorSelectRef.current.clearSelect();
                    }
                  }
                }, 10);
              }}
            />
          </div>
          {allowArithmetic &&
            (selectedField?.type === "number" ||
              selectedField?.type === "decimal") && (
              <div className="col-auto">
                <AICCArithmeticSelector
                  useColumnGroup={useColumnGroup}
                  columnList={columnList}
                  columnsWithGroup={columnsWithGroup}
                  selectedOp={data?.selectedLeftArOp}
                  leftArOpFieldGroup={data?.leftArOpFieldGroup}
                  value={data?.leftArOpValue}
                  selectValueSource={data?.leftArOpValueSource}
                  enabled={data?.leftArithmetic}
                  onChangeMultipleField={onChangeMultipleField}
                  evaluationValues={evaluationValues}
                  onCheckChanged={(checked) => {
                    onChangeField("leftArithmetic", checked);
                  }}
                  onOpChange={(val) => {
                    onChangeField("selectedLeftArOp", val);
                  }}
                  onChange={(val) => {
                    onChangeField("leftArOpValue", val);
                  }}
                  disabled={viewOnly}
                />
              </div>
            )}
          <div className="col-auto aicc-ruleBuilder__rule--operator">
            {selectedField && (
              <AICCRuleOperator
                ref={operatorSelectRef}
                value={data?.operator}
                operatorList={finalOperatorList}
                placeholder={t("aiccRuleBuilder.operatorPlaceholder")}
                noOptionsLabel={t("aiccRuleBuilder.noOperatorOptions")}
                onChange={(e) => {
                  onChangeMultipleField({
                    operator: e?.value,
                    expectedValue: e?.directValue ?? "",
                    noInput: e?.noInput,
                    value: "",
                    valueFieldGroup: "",
                    selectedRightArOp: "",
                    rightArOpValue: "",
                  });
                }}
                disabled={viewOnly}
              />
            )}
          </div>
          {showValueSource && (
            <div className="col-auto aicc-ruleBuilder__rule--value-source">
              <AICCValueSource
                value={data?.valueSource}
                onChange={(valueSource) => {
                  onChangeMultipleField({
                    expectedValue: "",
                    valueFieldGroup: "",
                    valueSource: valueSource,
                  });
                }}
                disabled={viewOnly}
              />
            </div>
          )}
          <div className="col-auto aicc-ruleBuilder__rule--value">
            {selectedField && operatorObj && !operatorObj?.noInput && (
              <AICCValueEditor
                fieldList={finalColumnList}
                valueSource={data?.valueSource}
                selectedField={selectedField}
                allowArithmetic={allowArithmetic}
                selectedOperator={operatorObj}
                selectedRightArOp={data?.selectedRightArOp}
                rightArOpValue={data?.rightArOpValue}
                value={data?.expectedValue}
                valueFieldGroup={data?.valueFieldGroup}
                useColumnGroup={useColumnGroup}
                columnsWithGroup={columnsWithGroup}
                rightLeftArithmetic={data?.rightLeftArithmetic}
                rightRightArithmetic={data?.rightRightArithmetic}
                isTodayDateChecked={data?.isTodayDateChecked}
                onChangeMultipleField={onChangeMultipleField}
                disabled={viewOnly}
                evaluationValues={evaluationValues}
                onCheckChanged={(checked, type) => {
                  if (type) {
                    const key =
                      type === "min"
                        ? "rightLeftArithmetic"
                        : "rightRightArithmetic";
                    onChangeField(key, checked);
                  } else {
                    onChangeField("rightLeftArithmetic", checked);
                  }
                }}
                onOpChange={(val, type) => {
                  if (type) {
                    const minMax = (data?.selectedRightArOp ?? "").split(",");
                    const changeIndex = type === "min" ? 0 : 1;
                    minMax[changeIndex] = val;

                    onChangeField("selectedRightArOp", minMax.join(","));
                  } else {
                    onChangeField("selectedRightArOp", val);
                  }
                }}
                onOpValueChange={(val, type) => {
                  if (type) {
                    const minMax = (data?.rightArOpValue ?? "").split(",");
                    const changeIndex = type === "min" ? 0 : 1;
                    minMax[changeIndex] = val;

                    onChangeField("rightArOpValue", minMax.join(","));
                  } else {
                    onChangeField("rightArOpValue", val);
                  }
                }}
                onValueFieldGroup={(value) => {
                  onChangeField("valueFieldGroup", value);
                }}
                onFieldChange={(key, value) => {
                  onChangeField(key, value);
                }}
                onChange={(value, valType) => {
                  if ((operatorObj?.value ?? "").includes("_between")) {
                    const minMax = (data?.expectedValue ?? "").split(",");
                    const changeIndex = valType === "min" ? 0 : 1;
                    minMax[changeIndex] = value;

                    onChangeField("expectedValue", minMax.join(","));
                  } else {
                    onChangeField("expectedValue", value);
                  }
                }}
              />
            )}
          </div>
          {isCaseCheckEnable && operatorObj?.showCaseInsensitiveCheck && (
            <div className="col-auto aicc-ruleBuilder__rule--case-insensitive">
              <input
                className="form-check-input"
                type="checkbox"
                defaultValue={false}
                checked={isCaseInsensitiveCheck}
                onChange={(e) => {
                  onChangeField("isCaseInsensitiveCheck", e.target.checked);
                  setIsCaseInsensitiveCheck(e.target.checked);
                }}
                disabled={viewOnly}
              />
              <label className="form-label">
                {t("aiccRuleBuilder.caseInsensitiveLabel")}
              </label>
            </div>
          )}
        </div>
      </div>
      <OverlayTrigger
        placement="auto"
        overlay={(props) => (
          <Tooltip id="delete-tooltip" {...props}>
            {t("aiccRuleBuilder.deleteQuery")}
          </Tooltip>
        )}
      >
        <span className="action-icon">
          <button onClick={onRemove} disabled={viewOnly}>
            <i className="fa-regular fa-trash-can"></i>
          </button>
        </span>
      </OverlayTrigger>
    </div>
  );
}
